import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  getCoachBoWeeklyCheckInActivityReport,
  getSampleWeeklyCheckInActivityData,
  getWeeklyCheckInCelebrateAndSupport,
  getWeeklyCheckInLineChartData,
  getWeeklyCheckInModuleStatus,
  selectCelebrateAndSupports,
  selectCoachBoWeeklyCheckInActivityReports,
  selectGetCoachBoWeeklyCheckInActivityReportStatus,
  selectGetWeeklyCheckInCelebrateAndSupportStatus,
  selectGetWeeklyCheckInLineChartDataStatus,
  selectGetWeeklyCheckInModuleStatus,
  selectSampleWeeklyCheckInActivityData,
  selectShowWeeklyCheckInActivityEmptyState,
  selectShowWeeklyCheckInSampleData,
  selectWeeklyCheckInCompanySettings,
  selectWeeklyCheckInCustomList,
  selectWeeklyCheckInLineChartData,
  setShowWeeklyCheckInSampleData,
} from "../slice";
import { useCallback, useEffect, useState } from "react";
import { trackFeatureViewed } from "utils/trackingFunctions";
import Toggle from "app/components/Toggle";
import WeeklyCheckInActivitySnapshot from "./WeeklyCheckInActivitySnapshot";
import CelebrateAndSupportContainer from "../CelebrateAndSupportContainer";
import { getTotalMembersWithWeeklyCheckInEnabled } from "app/containers/Dashboard/helpers";
import { selectAllUserSubordinates } from "app/containers/UserSettings/slice";
import { selectDepartments } from "app/containers/AdminConsole/slice";
import {
  selectAllCompanyUsersById,
  selectTeamsByTeamId,
} from "app/containers/Global/slice";

const AdminWeeklyCheckinSnapshot = () => {
  const dispatch = useAppDispatch();
  const coachBoWeeklyCheckInActivityReports = useAppSelector(
    selectCoachBoWeeklyCheckInActivityReports
  );
  const sampleWeeklyCheckInActivityData = useAppSelector(
    selectSampleWeeklyCheckInActivityData
  );
  const getCoachBoWeeklyCheckInActivityReportStatus = useAppSelector(
    selectGetCoachBoWeeklyCheckInActivityReportStatus
  );
  const getShowWeeklyCheckInActivityEmptyStateStatus = useAppSelector(
    selectGetWeeklyCheckInModuleStatus
  );
  const showWeeklyCheckInActivityEmptyState = useAppSelector(
    selectShowWeeklyCheckInActivityEmptyState
  );
  const weeklyCheckInLineChartData = useAppSelector(
    selectWeeklyCheckInLineChartData
  );
  const fetchingWeeklyCheckInLineChartDataStatus = useAppSelector(
    selectGetWeeklyCheckInLineChartDataStatus
  );
  const showWeeklyCheckInSampleData = useAppSelector(
    selectShowWeeklyCheckInSampleData
  );
  const getWeeklyCheckInCelebrateAndSupportStatus = useAppSelector(
    selectGetWeeklyCheckInCelebrateAndSupportStatus
  );
  const celebrateAndSupports = useAppSelector(selectCelebrateAndSupports);
  const weeklyCheckInCompanySettings = useAppSelector(
    selectWeeklyCheckInCompanySettings
  );
  const companyWeeklyCheckInCustomList = useAppSelector(
    selectWeeklyCheckInCustomList
  );
  const allUsersSubordinates = useAppSelector(selectAllUserSubordinates);
  const departments = useAppSelector(selectDepartments);
  const teamsById = useAppSelector(selectTeamsByTeamId);
  const usersById = useAppSelector(selectAllCompanyUsersById);

  const [
    weeklyCheckInActivityTimeInterval,
    setWeeklyCheckInActivityTimeInterval,
  ] = useState("Past 7 days");
  const [lineChartTimeInterval, setLineChartTimeInterval] =
    useState("Past 30 days");
  const [lineChartNavigationIndex, setLineChartNavigationIndex] = useState(1);

  useEffect(() => {
    const cadence = weeklyCheckInCompanySettings?.checkInFrequency ?? "weekly";
    if (cadence === "monthly") {
      setWeeklyCheckInActivityTimeInterval("Past 30 days");
      setLineChartTimeInterval("Past 365 days");
    } else if (cadence === "biweekly") {
      setWeeklyCheckInActivityTimeInterval("Past 14 days");
      setLineChartTimeInterval("Past 30 days");
    } else {
      setWeeklyCheckInActivityTimeInterval("Past 7 days");
      setLineChartTimeInterval("Past 30 days");
    }
  }, [weeklyCheckInCompanySettings?.checkInFrequency]);

  useEffect(() => {
    trackFeatureViewed("Weekly Check-In Activity Dashboard Card (Admin)");
  }, []);

  useEffect(() => {
    dispatch(getCoachBoWeeklyCheckInActivityReport());
    dispatch(getWeeklyCheckInModuleStatus());
    dispatch(
      getWeeklyCheckInLineChartData({
        timeInterval: "last30Days",
        index: 1,
      })
    );
  }, [dispatch]);

  // The sample data is large, so we only fetch it once the user turns on "Show Sample Data"
  useEffect(() => {
    if (showWeeklyCheckInSampleData) {
      dispatch(getSampleWeeklyCheckInActivityData());
    }
  }, [dispatch, showWeeklyCheckInSampleData]);
  const isLoadingSampleData =
    !!showWeeklyCheckInSampleData && !sampleWeeklyCheckInActivityData;

  const onGetWeeklyCheckInLineChartData = useCallback(
    ({ timeInterval, index }: { timeInterval: string; index: number }) => {
      dispatch(
        getWeeklyCheckInLineChartData({
          timeInterval,
          index,
        })
      );
    },
    [dispatch]
  );

  const totalUsersWithWeeklyCheckInEnabled =
    getTotalMembersWithWeeklyCheckInEnabled(
      weeklyCheckInCompanySettings,
      usersById,
      teamsById,
      departments,
      companyWeeklyCheckInCustomList,
      allUsersSubordinates
    );

  return (
    <WeeklyCheckInActivitySnapshot
      coachBoWeeklyCheckInActivityReports={
        showWeeklyCheckInSampleData && sampleWeeklyCheckInActivityData
          ? sampleWeeklyCheckInActivityData.checkInActivity
          : coachBoWeeklyCheckInActivityReports
      }
      merchandisingType="admin"
      getCoachBoWeeklyCheckInActivityReportStatus={
        isLoadingSampleData
          ? "loading"
          : getCoachBoWeeklyCheckInActivityReportStatus
      }
      showWeeklyCheckInActivityEmptyState={
        showWeeklyCheckInActivityEmptyState &&
        totalUsersWithWeeklyCheckInEnabled === 0 &&
        !showWeeklyCheckInSampleData
      }
      weeklyCheckInLineChartData={
        showWeeklyCheckInSampleData && sampleWeeklyCheckInActivityData
          ? sampleWeeklyCheckInActivityData.weeklyCheckInLineChartData
          : weeklyCheckInLineChartData
      }
      fetchingWeeklyCheckInLineChartDataStatus={
        isLoadingSampleData
          ? "loading"
          : fetchingWeeklyCheckInLineChartDataStatus
      }
      getShowWeeklyCheckInActivityEmptyStateStatus={
        getShowWeeklyCheckInActivityEmptyStateStatus
      }
      viewActivityPath="/AdminConsole/Dashboard/Check-In Activity"
      weeklyCheckInActivityTimeInterval={weeklyCheckInActivityTimeInterval}
      setWeeklyCheckInActivityTimeInterval={
        setWeeklyCheckInActivityTimeInterval
      }
      lineChartTimeInterval={lineChartTimeInterval}
      setLineChartTimeInterval={setLineChartTimeInterval}
      lineChartNavigationIndex={lineChartNavigationIndex}
      setLineChartNavigationIndex={setLineChartNavigationIndex}
      onGetWeeklyCheckInLineChartData={onGetWeeklyCheckInLineChartData}
      headerCornerContent={
        <div className="row-gap-12px align-items-center">
          <p className="grey-text">
            <b>Show Sample Data</b>
          </p>
          <Toggle
            handleToggle={() => {
              dispatch(
                setShowWeeklyCheckInSampleData(!showWeeklyCheckInSampleData)
              );
            }}
            isOn={!!showWeeklyCheckInSampleData}
          />
        </div>
      }
      additionalFooterContent={
        <CelebrateAndSupportContainer
          celebrateAndSupports={celebrateAndSupports}
          onGetWeeklyCheckInCelebrateAndSupport={(payload) =>
            dispatch(getWeeklyCheckInCelebrateAndSupport(payload))
          }
          isCelebrateAndSupportsBeingFetched={
            getWeeklyCheckInCelebrateAndSupportStatus === "loading" ||
            isLoadingSampleData
          }
          isSampleData={showWeeklyCheckInSampleData}
          sampleCelebrateAndSupports={
            sampleWeeklyCheckInActivityData?.celebrateAndSupport
          }
        />
      }
    />
  );
};

export default AdminWeeklyCheckinSnapshot;
